import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api from "@/lib/api";

function ContactRow({ c, allTags, onSaveTags, checked, onToggle }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState((c.tags || []).join(", "));
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const arr = draft.split(",").map((t) => t.trim()).filter(Boolean);
            await api.put(`/admin/contacts/${c.id}/tags`, { tags: arr });
            toast.success("Tags saved.");
            onSaveTags();
            setEditing(false);
        } catch { toast.error("Tag save failed."); }
        finally { setBusy(false); }
    };

    return (
        <tr className="border-b border-[color:var(--espresso)]/50" data-testid={`contact-row-${c.id}`}>
            <td className="py-3 w-8">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(c.id)}
                    className="accent-[color:var(--gold)] w-4 h-4 cursor-pointer"
                    data-testid={`contact-check-${c.id}`}
                />
            </td>
            <td className="py-3">
                <div className="font-serif text-lg text-[color:var(--soft-cream)]">{`${c.first_name} ${c.last_name}`.trim() || "—"}</div>
                <div className="text-xs text-[color:var(--cream-muted)]">{c.email}</div>
            </td>
            <td className="text-xs">{(c.events || []).join(", ") || "—"}</td>
            <td>
                {editing ? (
                    <div className="flex gap-1 items-center">
                        <input
                            list={`tag-suggest-${c.id}`}
                            className="input-line text-xs py-1 px-2 min-w-[180px]"
                            placeholder="vip, superfan, atlanta"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            data-testid={`contact-tags-input-${c.id}`}
                            autoFocus
                        />
                        <datalist id={`tag-suggest-${c.id}`}>
                            {allTags.map((t) => <option key={t.tag} value={t.tag} />)}
                        </datalist>
                        <button onClick={save} disabled={busy} className="text-[color:var(--gold)] text-xs px-2 disabled:opacity-40" data-testid={`contact-tags-save-${c.id}`}>Save</button>
                        <button onClick={() => { setEditing(false); setDraft((c.tags || []).join(", ")); }} className="text-[color:var(--cream-muted)] text-xs px-1">Cancel</button>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-1 items-center max-w-[280px]">
                        {(c.tags || []).map((t) => (
                            <span key={t} className="inline-block px-2 py-0.5 border border-[color:var(--gold)] text-[color:var(--gold)] text-[0.6rem] uppercase tracking-[0.14em]" data-testid={`contact-tag-${c.id}-${t}`}>{t}</span>
                        ))}
                        <button onClick={() => setEditing(true)} className="text-[color:var(--cream-muted)] text-xs hover:text-[color:var(--gold)]" data-testid={`contact-tags-edit-${c.id}`}>
                            {(c.tags || []).length === 0 ? "+ tag" : "edit"}
                        </button>
                    </div>
                )}
            </td>
            <td>
                {c.unsubscribed && c.bounce_status
                    ? <span className="text-[color:var(--wine)] text-xs uppercase tracking-[0.16em]" title={c.bounce_reason || ""}>{c.bounce_status === "complained" ? "Complained" : "Bounced"}</span>
                    : c.unsubscribed
                        ? <span className="text-[color:var(--wine)] text-xs uppercase tracking-[0.16em]">Unsubscribed</span>
                        : c.marketing_consent
                            ? <span className="text-[color:var(--gold)] text-xs uppercase tracking-[0.16em]">Opted in</span>
                            : <span className="text-[color:var(--cream-muted)] text-xs uppercase tracking-[0.16em]">No consent</span>}
            </td>
            <td className="text-xs text-[color:var(--cream-muted)]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
        </tr>
    );
}

export default function ContactsPanel() {
    const [contacts, setContacts] = useState([]);
    const [stats, setStats] = useState(null);
    const [allTags, setAllTags] = useState([]);
    const [q, setQ] = useState("");
    const [consent, setConsent] = useState("true");
    const [event, setEvent] = useState("");
    const [tag, setTag] = useState("");
    const [busy, setBusy] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [bulkTag, setBulkTag] = useState("");
    const fileRef = useRef(null);

    const refresh = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (consent && consent !== "all") params.set("consent", consent);
            if (event) params.set("event", event);
            if (tag) params.set("tag", tag);
            const [c, s, t] = await Promise.all([
                api.get(`/admin/contacts?${params.toString()}`),
                api.get("/admin/contacts/stats"),
                api.get("/admin/contacts/tags"),
            ]);
            setContacts(c.data);
            setStats(s.data);
            setAllTags(t.data);
            setSelected((prev) => new Set([...prev].filter((id) => c.data.some((row) => row.id === id))));
        } catch { toast.error("Could not load contacts."); }
    }, [q, consent, event, tag]);
    useEffect(() => { refresh(); }, [refresh]);

    const toggleOne = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleAll = () => setSelected((prev) => {
        if (prev.size === contacts.length && contacts.length > 0) return new Set();
        return new Set(contacts.map((c) => c.id));
    });
    const clearSelection = () => setSelected(new Set());

    const bulkApply = async (action) => {
        const t = bulkTag.trim();
        if (!t) { toast.error("Type a tag first."); return; }
        if (selected.size === 0) { toast.error("Select at least one contact."); return; }
        try {
            const { data } = await api.post("/admin/contacts/bulk-tag", {
                ids: [...selected],
                tag: t,
                action,
            });
            toast.success(`${action === "add" ? "Tagged" : "Un-tagged"} ${data.modified} contact${data.modified === 1 ? "" : "s"} · ${t}`);
            setBulkTag("");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk tag failed.");
        }
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        setImportResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImportResult(data);
            toast.success(`Imported ${data.imported} new · updated ${data.updated} existing.`);
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed.");
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div data-testid="contacts-panel">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                <div>
                    <p className="eyebrow">Marketing Contacts</p>
                    <p className="text-sm text-[color:var(--cream-muted)] mt-2 max-w-2xl">
                        Import past event attendees from SimpleTix (or any CSV). Only contacts with `marketing_consent = TRUE` are ever emailed. Unsubscribes are honored automatically and one-click.
                    </p>
                </div>
                <label className={`btn-gold btn-gold-solid cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`} data-testid="contacts-upload-label">
                    <Upload size={14} /> {busy ? "Importing…" : "Import CSV"}
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                        data-testid="contacts-upload-input"
                    />
                </label>
            </div>

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
                    {[["Total", stats.total], ["Opted In", stats.consented], ["Unsubscribed", stats.unsubscribed], ["Events", stats.by_event?.length || 0]].map(([k, v]) => (
                        <div key={k} className="border border-[color:var(--espresso)] p-4">
                            <p className="text-[0.6rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">{k}</p>
                            <p className="font-serif text-3xl mt-1">{v}</p>
                        </div>
                    ))}
                </div>
            )}

            {importResult && (
                <div className="border border-[color:var(--gold)] p-4 mb-6 text-sm" data-testid="contacts-import-result">
                    Imported <strong>{importResult.imported}</strong> new · updated <strong>{importResult.updated}</strong> existing · skipped {importResult.skipped_no_email + importResult.skipped_invalid} invalid rows.<br/>
                    <span className="text-xs text-[color:var(--cream-muted)]">
                        Detected columns: {Object.entries(importResult.detected_columns).map(([k, v]) => `${k} ← "${v}"`).join(" · ")}
                    </span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <input className="input-line" placeholder="Search email / name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contacts-search" />
                <select className="input-line bg-transparent" value={consent} onChange={(e) => setConsent(e.target.value)} data-testid="contacts-consent-filter">
                    <option value="true" className="bg-[color:var(--warm-black)]">Opted in only</option>
                    <option value="false" className="bg-[color:var(--warm-black)]">Opted out / unsubscribed</option>
                    <option value="all" className="bg-[color:var(--warm-black)]">All</option>
                </select>
                <select className="input-line bg-transparent" value={event} onChange={(e) => setEvent(e.target.value)} data-testid="contacts-event-filter">
                    <option value="" className="bg-[color:var(--warm-black)]">All events</option>
                    {(stats?.by_event || []).map((e) => (
                        <option key={e.event} value={e.event} className="bg-[color:var(--warm-black)]">{`${e.event} (${e.count})`}</option>
                    ))}
                </select>
                <select className="input-line bg-transparent" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="contacts-tag-filter">
                    <option value="" className="bg-[color:var(--warm-black)]">All tags</option>
                    {allTags.map((t) => (
                        <option key={t.tag} value={t.tag} className="bg-[color:var(--warm-black)]">{`${t.tag} (${t.count})`}</option>
                    ))}
                </select>
            </div>

            {contacts.length === 0 ? (
                <div className="border border-dashed border-[color:var(--espresso)] p-10 text-center" data-testid="contacts-empty">
                    <p className="text-sm text-[color:var(--cream-muted)]">
                        {stats?.total === 0 ? "No contacts yet. Import a SimpleTix CSV to get started." : "No contacts match those filters."}
                    </p>
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="text-[color:var(--gold)] text-left">
                        <tr className="border-b border-[color:var(--espresso)]">
                            <th className="py-3 w-8">
                                <input
                                    type="checkbox"
                                    checked={selected.size > 0 && selected.size === contacts.length}
                                    ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < contacts.length; }}
                                    onChange={toggleAll}
                                    className="accent-[color:var(--gold)] w-4 h-4 cursor-pointer"
                                    data-testid="contacts-select-all"
                                    aria-label="Select all visible contacts"
                                />
                            </th>
                            <th>Contact</th>
                            <th>Events</th>
                            <th>Tags</th>
                            <th>Consent</th>
                            <th>Added</th>
                        </tr>
                    </thead>
                    <tbody>
                        {contacts.map((c) => (
                            <ContactRow key={c.id} c={c} allTags={allTags} onSaveTags={refresh} checked={selected.has(c.id)} onToggle={toggleOne} />
                        ))}
                    </tbody>
                </table>
            )}

            {selected.size > 0 && (
                <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-30 flex flex-wrap items-center gap-3 border border-[color:var(--gold)] bg-[color:var(--warm-black)] px-4 py-3 shadow-2xl" data-testid="bulk-tag-bar">
                    <span className="text-xs uppercase tracking-[0.24em] text-[color:var(--gold)]">{selected.size} selected</span>
                    <input
                        list="bulk-tag-suggest"
                        className="input-line text-xs py-1 px-2 min-w-[180px]"
                        placeholder="Tag name (e.g. vip)"
                        value={bulkTag}
                        onChange={(e) => setBulkTag(e.target.value)}
                        data-testid="bulk-tag-input"
                    />
                    <datalist id="bulk-tag-suggest">
                        {allTags.map((t) => <option key={t.tag} value={t.tag} />)}
                    </datalist>
                    <button onClick={() => bulkApply("add")} className="btn-gold btn-gold-solid text-[0.65rem]" data-testid="bulk-tag-add">+ Add to all</button>
                    <button onClick={() => bulkApply("remove")} className="btn-gold text-[0.65rem]" data-testid="bulk-tag-remove">− Remove from all</button>
                    <button onClick={clearSelection} className="text-[color:var(--cream-muted)] text-xs" data-testid="bulk-tag-clear">Clear</button>
                </div>
            )}
        </div>
    );
}
